import { useState } from 'react'
import { PerTableActionButtons } from '../../components/custom_components/TableActionButtons'
import { EmployeeTagType, EmployeeAndTag, Country, EmployeeTagDto } from '../../types'
import { useTranslation } from 'react-i18next'
import PerText from '../../components/custom_components/Text'
import { RankingRetentionPopover } from './RankingRetentionPopover'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'
import { RankingPerformancePopover } from './RankingPerformancePopover'
import { rankingUiProps } from './ranking.uiprops'

export interface RankingTableActionButtonsProps {
  employeeFullName: string
  isRankingEnabled: boolean
  employeeAndTagData: EmployeeAndTag
  isHRSettings: boolean
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
  isCompanyManager: boolean
  settingsTopPercentage: number
  setModalOpenRemoveLow: (value: boolean) => void
  setModalDataRemoveLow: (data: EmployeeAndTag) => void
  setModalDataAddEntryLevelLow: (data: EmployeeAndTag) => void
  setModalOpenTagHistory: (value: boolean) => void
  companyId: string
  country: Country
}

export const RankingTableActionButtons: React.FunctionComponent<RankingTableActionButtonsProps> = ({
  employeeFullName,
  isRankingEnabled,
  employeeAndTagData,
  isHRSettings,
  isAllCompaniesSettings,
  isYourCompanyView,
  isCompanyManager,
  settingsTopPercentage,
  setModalDataRemoveLow,
  setModalOpenRemoveLow,
  setModalDataAddEntryLevelLow,
  setModalOpenTagHistory,
  companyId,
  country,
}) => {
  const [t] = useTranslation()
  const { isPerformanceTag, isImpactOfLossTag, isRiskOfLossTag } = rankingUiProps()
  const [isVisiblePerformancePopover, setIsVisiblePerformancePopover] = useState<boolean>(false)
  const [isVisibleRetentionPopover, setIsVisibleRetentionPopover] = useState<boolean>(false)

  const popoverHeadingInformationRow = (type: string) => (
    <PerText fontSize="xs" align="left" mb="xxs">
      <PerText display="inline" fontWeight="bold">
        {t(`rankingView.table.actions.tagEmployeePerformance.information.${type}.title`)}:{' '}
      </PerText>
      {t(`rankingView.table.actions.tagEmployeePerformance.information.${type}.description`)}
    </PerText>
  )

  return (
    <PerTableActionButtons
      destroyTooltipOnHide
      variant="pastel"
      buttons={[
        {
          icon: 'tagNo_fill',
          color: 'warning',
          tooltip: t('rankingView.table.actions.removeLow'),
          showOnlyWhen:
            isHRSettings &&
            employeeAndTagData.tags.find(
              (tag: EmployeeTagDto) => tag.type === EmployeeTagType.Low || tag.type === EmployeeTagType.EntryLevelLow,
            ) !== undefined,
          onClick: () => {
            setModalOpenRemoveLow(true)
            setModalDataRemoveLow(employeeAndTagData)
          },
        },
        {
          icon: 'tag_fill',
          color: 'warning',
          tooltip: t('rankingView.table.actions.addEntryLevelLow'),
          showOnlyWhen:
            isHRSettings &&
            employeeAndTagData.tags.find(
              (tag: EmployeeTagDto) => tag.type === EmployeeTagType.Low || tag.type === EmployeeTagType.EntryLevelLow,
            ) === undefined,
          onClick: () => {
            setModalDataAddEntryLevelLow(employeeAndTagData)
          },
        },
        {
          icon: 'view_fill',
          color: 'primary',
          tooltip: t('rankingView.table.actions.viewTagHistory'),
          onClick: () => {
            setModalOpenTagHistory(true)
          },
        },
        {
          icon: 'tag_fill',
          color: 'success',
          tooltip: t('rankingView.table.actions.tagEmployeePerformance.tooltip', { employee: employeeAndTagData.firstName }),
          showOnlyWhen: !isAllCompaniesSettings && !isHRSettings && !isYourCompanyView && isCompanyManager,
          popover: {
            title: (
              <div className="fullWidth">
                <PerFlexRow justify="space-between" align="middle" className="fullWidth" noWrap>
                  <PerText>{t('rankingView.table.actions.tagEmployeePerformance.title')}</PerText>
                  <PerButtonCircle
                    icon="info_fill"
                    color="secondary"
                    variant="pastel"
                    size="small"
                    className="disable-shadow"
                    tooltip={
                      <>
                        {popoverHeadingInformationRow('top')}
                        {popoverHeadingInformationRow('speaker')}
                        {popoverHeadingInformationRow('sales')}
                        {popoverHeadingInformationRow('coach')}
                        {popoverHeadingInformationRow('expert')}
                        {popoverHeadingInformationRow('untagged')}
                        {popoverHeadingInformationRow('low')}
                      </>
                    }
                  />
                </PerFlexRow>
                <PerText appearance="label-small">
                  {t('rankingView.table.actions.tagEmployeePerformance.subtitle', {
                    employee: `${employeeAndTagData.firstName} ${employeeAndTagData.lastName}`,
                  })}
                </PerText>
              </div>
            ),
            content: (
              <RankingPerformancePopover
                employeeFullName={employeeFullName}
                employeeFirstName={employeeAndTagData.firstName}
                employeeLastName={employeeAndTagData.lastName}
                isRankingEnabled={isRankingEnabled}
                tags={employeeAndTagData.tags.filter((t) => isPerformanceTag(t.type as EmployeeTagType))}
                settingsTopPercentage={settingsTopPercentage}
                employeeId={employeeAndTagData.id}
                companyId={companyId}
                country={country}
                isVisible={isVisiblePerformancePopover}
                setIsVisible={setIsVisiblePerformancePopover}
              />
            ),
            visible: isVisiblePerformancePopover,
            onVisibleChange: () => setIsVisiblePerformancePopover(!isVisiblePerformancePopover),
          },
        },
        {
          icon: 'leave_fill',
          color: 'warning',
          tooltip: t('rankingView.table.actions.tagEmployeeRetention.tooltip', { employee: employeeAndTagData.firstName }),
          showOnlyWhen: !isAllCompaniesSettings && !isHRSettings && !isYourCompanyView && isCompanyManager,
          popover: {
            title: (
              <div>
                <PerText display="block">{t('rankingView.table.actions.tagEmployeeRetention.title')}</PerText>
                <PerText appearance="label-small" display="block">
                  {t('rankingView.table.actions.tagEmployeeRetention.subtitle', {
                    employee: `${employeeAndTagData.firstName} ${employeeAndTagData.lastName}`,
                  })}
                </PerText>
              </div>
            ),
            content: (
              <RankingRetentionPopover
                isVisible={isVisibleRetentionPopover}
                setIsVisible={setIsVisibleRetentionPopover}
                companyId={companyId}
                country={country}
                employeeId={employeeAndTagData.id}
                impactTags={employeeAndTagData.tags.filter((t) => isImpactOfLossTag(t.type as EmployeeTagType))}
                riskTags={employeeAndTagData.tags.filter((t) => isRiskOfLossTag(t.type as EmployeeTagType))}
              />
            ),
            visible: isVisibleRetentionPopover,
            onVisibleChange: () => setIsVisibleRetentionPopover(!isVisibleRetentionPopover),
          },
        },
      ]}
    />
  )
}
